import React from 'react';
import { Box, Typography } from '@curves/ui-base';

/** 手機版唯讀欄位：上方 label 小字，下方 value 大字 */
const Field = ({ label, children }) => (
    <Box className="cp-effect-field">
        <Typography className="cp-effect-field-label">{label}</Typography>
        <Typography className="cp-effect-field-value">{children || '—'}</Typography>
    </Box>
);

/** Section 包裝：標題列 + 內容區 */
const Section = ({ title, children }) => (
    <Box className="cp-effect-section">
        <Box className="cp-effect-section-header">
            <Typography className="cp-effect-section-title">{title}</Typography>
        </Box>
        <Box className="cp-effect-section-body">{children}</Box>
    </Box>
);

const toDateStr = (iso) => (iso ? iso.slice(0, 10).replace(/-/g, '/') : '');

/** 變更單基本資訊（effectNo / effectDate / effectStatusText / employeeName） */
export const EffectInfoSection = ({ sourceData = {} }) => {
    const { effectNo, effectStatusText, effectDate, effectCreateDate, employeeName } = sourceData;
    return (
        <Section title="變更單資訊">
            <Box className="cp-effect-field-grid">
                <Field label="變更單編號">{effectNo}</Field>
                <Field label="變更單狀態">{effectStatusText}</Field>
                <Field label="生效日期">{toDateStr(effectDate)}</Field>
                <Field label="建單日期">{toDateStr(effectCreateDate)}</Field>
                {employeeName && <Field label="建單擔當">{employeeName}</Field>}
            </Box>
        </Section>
    );
};

/** ITEMS / CHANGEITEMS：訂購商品 + 訂單金額 */
export const ProductSection = ({ sourceData = {} }) => {
    const { items = [] } = sourceData;
    const total = items.reduce((sum, item) => sum + (item.salePrice ?? 0) * (item.quantity ?? 0), 0);

    return (
        <Section title="訂購商品">
            {items.length === 0 ? (
                <Typography className="cp-effect-field-value">（無商品資料）</Typography>
            ) : (
                <>
                    {items.map((item, index) => (
                        <Box key={item.productID ?? index} className="cp-effect-product-row">
                            <Typography className="cp-effect-product-name">{item.productName}</Typography>
                            <Typography className="cp-effect-product-qty">x{item.quantity}</Typography>
                        </Box>
                    ))}
                    <Box className="cp-effect-product-total-row">
                        <Typography className="cp-effect-product-total-label">訂單金額</Typography>
                        <Typography className="cp-effect-product-total-value">${total.toLocaleString()}</Typography>
                    </Box>
                </>
            )}
        </Section>
    );
};

/** SHIPPINGDAY：出貨週期 */
export const ShippingSection = ({ sourceData = {} }) => {
    const { shippingDayText } = sourceData;
    return (
        <Section title="出貨週期">
            <Field label="出貨週期">{shippingDayText}</Field>
        </Section>
    );
};

/** RECEIVER：收件人資訊 */
export const ReceiverSection = ({ sourceData = {} }) => {
    const { receiverName, receiverMobile, receiverPostCode = '', receiverAddress = '', receiverNotes } = sourceData;
    return (
        <Section title="收件人資訊">
            <Box className="cp-effect-field-grid">
                <Field label="收件人">{receiverName}</Field>
                <Field label="收件手機">{receiverMobile}</Field>
            </Box>
            <Field label="收件地址">{`${receiverPostCode} ${receiverAddress}`.trim()}</Field>
            {receiverNotes && <Field label="備註">{receiverNotes}</Field>}
        </Section>
    );
};


/** STOP：暫停 */
export const SuspensionSection = ({ sourceData = {} }) => {
    const { suspendStartDate, suspendEndDate, suspendReasonText, suspendNote } = sourceData;
    const periodStr =
        suspendStartDate && suspendEndDate
            ? `${toDateStr(suspendStartDate)} ～ ${toDateStr(suspendEndDate)}`
            : toDateStr(suspendStartDate);
    return (
        <Section title="暫停">
            <Field label="暫停期間">{periodStr}</Field>
            <Field label="暫停原因">{suspendReasonText}</Field>
            {suspendNote && <Field label="暫停原因補充說明">{suspendNote}</Field>}
        </Section>
    );
};

/** QUIT：退訂 */
export const TerminateSection = ({ sourceData = {} }) => {
    const { unsubscribeDate, unsubscribeReasonText, unsubscribeNote } = sourceData;
    return (
        <Section title="退訂">
            <Box className="cp-effect-field-grid">
                {unsubscribeDate && <Field label="退訂日期">{toDateStr(unsubscribeDate)}</Field>}
                <Field label="退訂原因">{unsubscribeReasonText}</Field>
            </Box>
            {unsubscribeNote && <Field label="退訂原因補充說明">{unsubscribeNote}</Field>}
        </Section>
    );
};

/** RESTOP：回復暫停 */
export const ResumeSection = ({ sourceData = {} }) => {
    const { originalSuspendStartDate, originalSuspendEndDate, reSuspendDate } = sourceData;
    const originalPeriod =
        originalSuspendStartDate && originalSuspendEndDate
            ? `${originalSuspendStartDate.slice(0, 7).replace(/-/g, '/')} ～ ${originalSuspendEndDate.slice(0, 7).replace(/-/g, '/')}`
            : '';
    const resumeMonth = reSuspendDate ? reSuspendDate.slice(0, 7).replace(/-/g, '/') : '';
    return (
        <Section title="暫停恢復">
            <Field label="原暫停期間">{originalPeriod}</Field>
            <Field label="恢復出貨月份">{resumeMonth}</Field>
        </Section>
    );
};
